import { useLocalization } from 'model/localization.model';
import { FunctionalComponent, h } from 'preact';
import { styled } from 'linaria/react';
import { css } from 'linaria';
import Webcam from 'libs/webcam';

import { useEffect, useRef, useState } from 'preact/hooks';
import { Photo } from 'model/domain';
import { HiveIcon } from 'ui/icons/hive-icon';

const video = css`
	display: block;
  height: 100%;
  z-index: 0;
  /* Use \`object-fit: cover\` on every browser but Edge */
  @supports not (-ms-ime-align:auto) {
    width:100%;
    top: 0;
    left: 0;
    margin: auto;
    object-fit: cover;
  }
`;

const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	return new Blob(byteArrays, { type: contentType });
};

export interface CameraProps {
	cameraType: 'front' | 'back';
	onCapture: (photo: Photo) => void;
	className?: string;
}

export const Camera: FunctionalComponent<CameraProps> = (props) => {
	const { cameraType, onCapture, className, children } = props;
	const cameraRef = useRef<any>(null);
	const containerRef = useRef(null);
	const t = useLocalization();

	const [isCameraOpen, changeIsCameraOpen] = useState(false);

	useEffect(() => {
		const container = containerRef.current;
		if (!container || !container.children[0]) { return; }

		container.children[0].addEventListener('click', () => changeIsCameraOpen(true));
	}, []);

	const handleCreatePhoto = async () => {
		setTimeout(async () => {
			const camera = cameraRef.current;
			if (!camera) {
				return;
			}

			const photo = await camera.getScreenshot();
			const [meta, base64] = photo.split(',');
			const contentType = meta.match(/data:(.+);.+/);
			changeIsCameraOpen(false);
			onCapture(b64toBlob(base64, contentType));
		}, 50);
	};

	return (
		<div ref={containerRef} className={className}>
			{children}
			{isCameraOpen && (
				<CameraContainer>
					<Webcam
						ref={cameraRef}
						className={video}
						audio={false}
						height={1280}
						facingMode={cameraType === 'front' ? 'user' : 'environment'}
						onUserMedia={() => {}}
						onFailure={() => {}}
					/>
					<Overlay/>
					<Title>
						{t('mobile.selfie.header')}
					</Title>
					<CaptureButton onClick={handleCreatePhoto}/>
					<CancelButton
						onClick={() => changeIsCameraOpen(false)}
					>
						<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M8.48514 8.00022L12.9723 3.51308L13.8976 2.58774C14.0341 2.45123 14.0341 2.22941 13.8976 2.09291L12.9075 1.10282C12.771 0.966312 12.5492 0.966312 12.4127 1.10282L7.00022 6.5153L1.58774 1.10238C1.45123 0.965874 1.22941 0.965874 1.09291 1.10238L0.102378 2.09247C-0.0341261 2.22898 -0.0341261 2.4508 0.102378 2.5873L5.5153 8.00022L0.102378 13.4127C-0.0341261 13.5492 -0.0341261 13.771 0.102378 13.9075L1.09247 14.8976C1.22898 15.0341 1.4508 15.0341 1.5873 14.8976L7.00022 9.48514L11.4874 13.9723L12.4127 14.8976C12.5492 15.0341 12.771 15.0341 12.9075 14.8976L13.8976 13.9075C14.0341 13.771 14.0341 13.5492 13.8976 13.4127L8.48514 8.00022Z"
								fill="var(--button-outline-border)"/>
						</svg>
					</CancelButton>
					<CameraFooter>
						<span>Powered by</span>
						<HiveIcon/>
					</CameraFooter>
				</CameraContainer>
			)}
		</div>
	);
};

const CameraFooter = styled('div')`
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	bottom: 6px;
	width: 100%;
	min-height: 20px;
	
	span {
		color: #BFBFBF;
		margin-right: 8px;
	}
	
	svg {
		color: #BFBFBF;
	}
`;
const Title = styled('div')`
	position: absolute;
	top: 16px;
	left: 16px;
	font-weight: 600;
	font-size: 16px;
	line-height: 24px;
	color: var(--button-outline-border);
	z-index: 1000;
`;
const Overlay = styled('div')`
	background-size: 190%;
	background-position: center 55%;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='740' height='1800'%3E%3Cpath fill-opacity='.80' d='M0 1800V0h740v1800H0zm237-914.056c0 71.601 59.104 175.672 132.021 175.672s132.022-101.453 132.022-175.672c0-83.917-59.107-151.944-132.022-151.944C296.108 734 237 802.027 237 885.944z'/%3E%3Cpath fill='none' stroke='%23FFF' stroke-width='3' d='M237 885.944c0 71.601 59.104 175.672 132.021 175.672s132.022-101.453 132.022-175.672c0-83.917-59.107-151.944-132.022-151.944C296.108 734 237 802.027 237 885.944z'/%3E%3C/svg%3E%0A");
`;
const CameraContainer = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	background: black;
`;
const CancelButton = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	background: none;
	border: none;
	outline: none;
	color: white;
	line-height: 64px !important;
	font-size: 17px !important;
	z-index: 1000;
`;
const CaptureButton = styled.button`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	bottom: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 75px;
	height: 75px;
	background: transparent;  
	border-radius: 50%;
	outline: none;
	border: 3px solid rgb(254, 255, 255);
	box-sizing: border-box;
	transition: .2s ease-in-out;
	  
	&:active {
	 transform: scale(.97)
	}
	  
	&:before {
	  content: '';
	  display: block;
	  min-width: 66px;
	  min-height: 66px;
	  background: rgb(254, 255, 255);
	  border: none;
	  border-radius: 50%;
	  margin: 0 auto;
	  box-sizing: border-box;
	}
`;
